import React from 'react';
import styled from 'styled-components';
import { remCalc } from '@lux/helpers';

import Anchor from '../Anchor';
import Grid from '../Grid';
import Column from '../Column';
import List from '../List';
import ListItem from '../ListItem';
import Row from '../Row';
import Theme from '../../theme';

import Avatar from './Avatar';
import DesktopMenu from './DesktopMenu';
import Logo from './Logo';
import Search from './Search';
import ShoppingCart from './ShoppingCart';
import VeriSignLogo from './VeriSignLogo';

import IconLogin from '../../assets/svgs/header/login.svg';

const SCHeaderDesktop = styled.div`
  display: none;
  width: 100%;
  z-index: 99;
  ${Theme.media.lg`
  display: block;
  `};
`;

const SCMenuItemList = styled(List)`
  float: right;
  display: flex;
  align-items: center;
  height: 100%;
  min-height: ${remCalc(28)};
`;

const SCAnchorBlack = styled(Anchor)`
  color: ${Theme.colours.black};
  font-size: ${Theme.fonts.sizes.small};
  display: block;

  &:not([disabled]) {
    &:hover {
      color: ${Theme.colours.black};
    }
  }

  & svg {
    float: right;
  }
`;

const SCFlexColumnAuto = styled(Column)`
  flex: 1 0 auto;
`;

const SCFlexColumnAutoEnd = styled(Column)`
  flex: 0 0 auto;
`;

const SCMenuItemListPrimary = styled(List)`
  padding-bottom: ${remCalc(6)};

  > li {
    margin-left: 0;
  }
`;

const RHSControlListItem = styled(ListItem)`
  display: flex;
  align-items: center;

  &:first-child {
    margin: 0;
  }
`;

const SCLogin = styled(Anchor)`
  display: block;
`;

const SCIconLogin = styled(IconLogin)`
  vertical-align: middle;
`;

const DesktopTopRHSMenuItems = ({ baseUrl, items }) => {
  return items.map((item, key) => {
    return (
      <ListItem key={key}>
        {item.active ? (
          <SCAnchorBlack href={`${baseUrl}${item.href}`}>
            {item.title}
          </SCAnchorBlack>
        ) : (
          <SCAnchorBlack secondary href={`${baseUrl}${item.href}`}>
            {item.title}
          </SCAnchorBlack>
        )}
      </ListItem>
    );
  });
};

const DesktopRHSControls = ({
  baseUrl,
  showSearch,
  showLoginButton,
  shoppingCartUrl,
  isSearchActive,
  onSearchClick,
  loginUrl,
  showVeriSignLogo,
  showShoppingCart,
  showAvatar,
  avatarMenuItems
}) => (
  <SCMenuItemList isInline>
    {showSearch && (
      <RHSControlListItem>
        <Search isActive={isSearchActive} onClick={onSearchClick} />
      </RHSControlListItem>
    )}
    {showLoginButton && (
      <RHSControlListItem>
        <SCLogin
          secondary
          href={`${baseUrl}${loginUrl}`}
          ariaLabel="Login Button"
        >
          <SCIconLogin size={40} />
        </SCLogin>
      </RHSControlListItem>
    )}
    {showAvatar && (
      <RHSControlListItem>
        <Avatar baseUrl={baseUrl} menu={avatarMenuItems} />
      </RHSControlListItem>
    )}
    {showShoppingCart && (
      <RHSControlListItem>
        <ShoppingCart
          baseUrl={baseUrl}
          isLoggedIn={showAvatar}
          url={shoppingCartUrl}
        />
      </RHSControlListItem>
    )}
    {showVeriSignLogo && (
      <RHSControlListItem>
        <VeriSignLogo />
      </RHSControlListItem>
    )}
  </SCMenuItemList>
);

const DesktopHeader = ({
  baseUrl,
  menuItems,
  logoLink,
  logoAlt,
  showSearch,
  loginUrl,
  shoppingCartUrl,
  isSearchActive,
  onSearchClick,
  menuHoverKey,
  onMenuMouseEnter,
  onMenuMouseLeave,
  showTopRHSMenuItems,
  showVeriSignLogo,
  showLoginButton,
  showShoppingCart,
  showAvatar,
  avatarMenuItems,
  firstLevelLinks
}) => (
  <SCHeaderDesktop>
    <Grid>
      <Row>
        <Column sm={9} lg={8} />
        <Column sm={3} lg={4}>
          <SCMenuItemList isInline={true}>
            {/* Top right hand corner primary menu items */}
            {showTopRHSMenuItems && (
              <DesktopTopRHSMenuItems
                baseUrl={baseUrl}
                items={firstLevelLinks}
              />
            )}
          </SCMenuItemList>
        </Column>
      </Row>
      <Row middle="sm">
        <SCFlexColumnAuto>
          <SCMenuItemListPrimary isInline={true}>
            <ListItem>
              {/* Desktop logo */}
              <Logo baseUrl={baseUrl} link={logoLink} alt={logoAlt} />
            </ListItem>
            {/* Primary menu items in desktop */}
            <DesktopMenu
              baseUrl={baseUrl}
              menuItems={menuItems}
              menuHoverKey={menuHoverKey}
              onMenuMouseEnter={onMenuMouseEnter}
              onMenuMouseLeave={onMenuMouseLeave}
            />
          </SCMenuItemListPrimary>
        </SCFlexColumnAuto>
        <SCFlexColumnAutoEnd>
          {/* Right hand side items */}
          <DesktopRHSControls
            baseUrl={baseUrl}
            avatarMenuItems={avatarMenuItems}
            isSearchActive={isSearchActive}
            loginUrl={loginUrl}
            shoppingCartUrl={shoppingCartUrl}
            showAvatar={showAvatar}
            showLoginButton={showLoginButton}
            showSearch={showSearch}
            showShoppingCart={showShoppingCart}
            showVeriSignLogo={showVeriSignLogo}
            onSearchClick={onSearchClick}
          />
        </SCFlexColumnAutoEnd>
      </Row>
    </Grid>
  </SCHeaderDesktop>
);

export default DesktopHeader;
